import { useMemo } from "react";

/**
 * 아직 한 권도 담지 않은 사람의 홈.
 *
 * 여태 이 화면은 평소 홈을 그대로 그리고 값만 0 으로 채웠다. 그래서
 * 가입 직후 0 이 여섯 개 보였다 — 행성 안, 칩 셋, 탭 셋. 거기에
 * '10권 더 담으면 행성 색을 고를 수 있어요' 와 빈 곳으로 가는 버튼까지
 * 붙어, 가진 게 없다는 사실을 여섯 번 확인시켜 주었다.
 *
 * 셀 것이 없으면 세지 않는다. 대신 할 일 하나와, 지금 이 우주에서
 * 실제로 읽히는 책을 보여준다 — 205 라는 숫자보다 표지 여섯 장이 세다.
 *
 * 문턱은 0 권 하나뿐이다. 한 권이라도 담으면 평소 홈으로 돌아간다.
 * 3권·5권에서 무언가 나타나게 하면 왜 나타났는지 알 수 없다.
 */
const SHOW = 6;

const EmptyPlanet = ({ allBooks = [], onAddClick, onAddWishClick, onQuickAdd, onShowUserSearch, onShowBookWorld, otherPlanets = 0 }) => {
  /* 아무도 끝내지 않은 책 = 지금 읽히는 중. 표지가 없는 책은 뺀다 —
     이 자리의 요점이 표지라서 이모지로 대신하면 뜻이 없다. */
  const reading = useMemo(() => {
    const rows = (allBooks || []).filter((b) => {
      if (!b.cover) return false;
      const registered = Object.keys(b.registeredBy || {});
      const completed = b.completedBy || {};
      return registered.some((id) => !completed[id]);
    });
    /* 순서를 난수로 섞지 않는다. 새로 고칠 때마다 바뀌면 방금 본 책을
       다시 찾을 수 없다. 최근에 들어온 책이 앞에 온다. */
    return [...rows]
      .sort((a, b) => String(b.addedDate ?? "").localeCompare(String(a.addedDate ?? "")))
      .slice(0, SHOW);
  }, [allBooks]);

  const total = useMemo(
    () => (allBooks || []).filter((b) => {
      const registered = Object.keys(b.registeredBy || {});
      const completed = b.completedBy || {};
      return registered.some((id) => !completed[id]);
    }).length,
    [allBooks]
  );

  return (
    <div className="ep">
      {/* 숫자를 넣지 않는다. 0 을 크게 박아둘 자리가 아니다. */}
      <div className="ep-planet">
        <div className="planet-ring" />
        <div className="my-planet" />
      </div>

      {/* 영문 라벨은 아직 자기 행성이 없는 사람에게 아무 뜻이 없다.
          세계관을 첫 문장에서 같이 말한다. */}
      <div className="ep-lead">
        읽은 책 한 권이 별이 되고,<br />쌓이면 나만의 행성이 됩니다
      </div>

      <button className="ep-cta" onClick={onAddClick}>＋ 첫 책 등록하기</button>
      {/* 부담이 덜한 쪽이지만 주된 길은 아니라 글자로 낮춘다 */}
      <button className="ep-wish" onClick={onAddWishClick}>읽고 싶은 책부터 담아두기</button>

      {reading.length > 0 && (
        <div className="ep-sec">
          <div className="ep-sec-t">지금 이 우주에서 읽히는 책</div>
          <div className="ep-books">
            {reading.map((b) => (
              <button key={b.id} className="ep-book" onClick={() => onQuickAdd?.(b)}>
                <img src={b.cover} alt="" loading="lazy" />
                <span className="ep-book-t">{b.title}</span>
              </button>
            ))}
          </div>
          {/* 책마다 '1명이 읽는 중' 을 붙이지 않는다. 지금은 대부분 1명이라
              낱개로 세면 오히려 텅 빈 것을 확인시켜 준다. 합계 한 줄이 세다. */}
          <div className="ep-sum">{total}권이 지금 읽히고 있어요 · 눌러서 나도 담기</div>
        </div>
      )}

      {/* 아직 책이 없는 사람에게 남의 행성 구경은 두 번째 일이라 글자로 둔다 */}
      <div className="ep-links">
        <button className="ep-link" onClick={onShowUserSearch}>
          🔭 다른 행성 보기{otherPlanets > 0 ? ` ${otherPlanets}` : ""}
        </button>
        <button className="ep-link" onClick={onShowBookWorld}>📚 다른 책 탐험하기</button>
      </div>
    </div>
  );
};

export default EmptyPlanet;
